<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useSessionStore } from "@/stores/session-store";

interface Role {
  value: string;
  label: string;
  description: string;
}

const props = defineProps<{
  modelValue: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const sessionStore = useSessionStore();

const allRoles: Role[] = [
  {
    value: "SuperAdmin",
    label: "Super Admin",
    description: "Full system access and management"
  },
  {
    value: "OrgAdmin",
    label: "Organization Admin",
    description: "Manage users and organization settings"
  },
  {
    value: "OrgMember",
    label: "Organization Member",
    description: "Create and edit workflows and products"
  },
  {
    value: "Partner",
    label: "Partner",
    description: "External partner with access to shared resources"
  }
];

// Filter out SuperAdmin role for non-SuperAdmins
const roles = computed(() => {
  if (sessionStore.session?.isSuperAdmin) {
    return allRoles;
  }
  return allRoles.filter(r => r.value !== "SuperAdmin");
});

const selectedRole = ref(props.modelValue || "OrgMember");

watch(selectedRole, (newValue) => {
  emit("update:modelValue", newValue);
});

watch(() => props.modelValue, (newValue) => {
  selectedRole.value = newValue;
});

const selectedRoleInfo = computed(() => {
  return allRoles.find(r => r.value === selectedRole.value);
});
</script>

<template>
  <div class="role-selector">
    <label class="form-label">User Role</label>
    <select
      v-model="selectedRole"
      class="form-select"
      :disabled="disabled"
    >
      <option value="">Select a role...</option>
      <option
        v-for="role in roles"
        :key="role.value"
        :value="role.value"
      >
        {{ role.label }}
      </option>
    </select>

    <div v-if="selectedRoleInfo" class="alert alert-info mt-2 mb-0">
      <strong>{{ selectedRoleInfo.label }}:</strong>
      {{ selectedRoleInfo.description }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.alert-info {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}
</style>
